import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brain, Sparkles, Globe, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/blog/future-ai-service.jpg";
import BlogArticleLayout from "@/components/BlogArticleLayout";

const FutureOfAICustomerService = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.futureOfAICustomerService.metaTitle')}</title>
        <meta name="description" content={t('blog.futureOfAICustomerService.metaDescription')} />
        <meta name="keywords" content="future of AI customer service, AI customer service trends, AI call center future, customer service automation 2030, AI support predictions" />
        <link rel="canonical" href="https://call24x7.ai/blog/future-of-ai-customer-service" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7 Team" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://call24x7.ai/blog/future-of-ai-customer-service" />
        <meta property="og:title" content={t('blog.futureOfAICustomerService.title')} />
        <meta property="og:description" content={t('blog.futureOfAICustomerService.metaDescription')} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('blog.futureOfAICustomerService.title'),
            "description": t('blog.futureOfAICustomerService.metaDescription'),
            "author": { "@type": "Organization", "name": "Call24x7.AI" },
            "publisher": { "@type": "Organization", "name": "Call24x7.AI" },
            "datePublished": "2025-03-05",
            "dateModified": "2025-03-05"
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.futureOfAICustomerService.title')}
        category={t('blog.categories.industryTrends')}
        date="March 5, 2025"
        readTime={`9 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="Future of AI customer service"
      >
        <p className="text-xl text-muted-foreground mb-6">
          {t('blog.futureOfAICustomerService.intro')}
        </p>

        <div className="grid md:grid-cols-4 gap-4 my-8">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Brain className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-lg font-bold">{t('blog.futureOfAICustomerService.trend1Short')}</div>
              <p className="text-sm text-muted-foreground">{t('blog.futureOfAICustomerService.trend1Desc')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Sparkles className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-lg font-bold">{t('blog.futureOfAICustomerService.trend2Short')}</div>
              <p className="text-sm text-muted-foreground">{t('blog.futureOfAICustomerService.trend2Desc')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Globe className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-lg font-bold">{t('blog.futureOfAICustomerService.trend3Short')}</div>
              <p className="text-sm text-muted-foreground">{t('blog.futureOfAICustomerService.trend3Desc')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Shield className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-lg font-bold">{t('blog.futureOfAICustomerService.trend4Short')}</div>
              <p className="text-sm text-muted-foreground">{t('blog.futureOfAICustomerService.trend4Desc')}</p>
            </CardContent>
          </Card>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.futureOfAICustomerService.prediction1Title')}</h2>
        <p>{t('blog.futureOfAICustomerService.prediction1Desc')}</p>
        <p className="mt-4"><strong>{t('blog.futureOfAICustomerService.impact')}:</strong> {t('blog.futureOfAICustomerService.prediction1Impact')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.futureOfAICustomerService.prediction2Title')}</h2>
        <p>{t('blog.futureOfAICustomerService.prediction2Desc')}</p>
        <p className="mt-4"><strong>{t('blog.futureOfAICustomerService.impact')}:</strong> {t('blog.futureOfAICustomerService.prediction2Impact')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.futureOfAICustomerService.prediction3Title')}</h2>
        <p>{t('blog.futureOfAICustomerService.prediction3Desc')}</p>
        <p className="mt-4"><strong>{t('blog.futureOfAICustomerService.impact')}:</strong> {t('blog.futureOfAICustomerService.prediction3Impact')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.futureOfAICustomerService.prediction4Title')}</h2>
        <p>{t('blog.futureOfAICustomerService.prediction4Desc')}</p>
        <p className="mt-4"><strong>{t('blog.futureOfAICustomerService.impact')}:</strong> {t('blog.futureOfAICustomerService.prediction4Impact')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.futureOfAICustomerService.prediction5Title')}</h2>
        <p>{t('blog.futureOfAICustomerService.prediction5Desc')}</p>
        <p className="mt-4"><strong>{t('blog.futureOfAICustomerService.impact')}:</strong> {t('blog.futureOfAICustomerService.prediction5Impact')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.futureOfAICustomerService.prediction6Title')}</h2>
        <p>{t('blog.futureOfAICustomerService.prediction6Desc')}</p>
        <p className="mt-4"><strong>{t('blog.futureOfAICustomerService.impact')}:</strong> {t('blog.futureOfAICustomerService.prediction6Impact')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.futureOfAICustomerService.prepareTitle')}</h2>
        <ol className="list-decimal pl-6 my-4 space-y-3">
          <li><strong>{t('blog.futureOfAICustomerService.prepare1Title')}:</strong> {t('blog.futureOfAICustomerService.prepare1Desc')}</li>
          <li><strong>{t('blog.futureOfAICustomerService.prepare2Title')}:</strong> {t('blog.futureOfAICustomerService.prepare2Desc')}</li>
          <li><strong>{t('blog.futureOfAICustomerService.prepare3Title')}:</strong> {t('blog.futureOfAICustomerService.prepare3Desc')}</li>
          <li><strong>{t('blog.futureOfAICustomerService.prepare4Title')}:</strong> {t('blog.futureOfAICustomerService.prepare4Desc')}</li>
          <li><strong>{t('blog.futureOfAICustomerService.prepare5Title')}:</strong> {t('blog.futureOfAICustomerService.prepare5Desc')}</li>
        </ol>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.futureOfAICustomerService.bottomLineTitle')}</h2>
        <p>{t('blog.futureOfAICustomerService.bottomLineDesc')}</p>

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.futureOfAICustomerService.ctaTitle')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.futureOfAICustomerService.ctaDesc')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.futureOfAICustomerService.ctaButton')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default FutureOfAICustomerService;
